// Tennis UI kit — thin, presentational primitives for the Tennis
// Intelligence Module surfaces. All styling lives in src/styles/tennis.css
// (namespaced under `.tn`); these components only compose markup + classes.
//
// Editorial trading-terminal language, matched to the Signal Window flagship:
// Playfair Display titles + numerical moments, JetBrains Mono micro-kickers,
// Inter body, with the --sz-tennis (deep court green) accent used sparingly.
//
// Doctrine (binding): surfaces show OUTPUTS and factual/educational reference
// only — never fabricated tennis data, never model weights / exact edge
// thresholds / per-market win rates.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../../components/icons/Icon.tsx";

// Roland Garros 2026 — Round 1 (PRD-confirmed pilot start).
export const R1_DATE = new Date("2026-05-24T11:00:00+02:00");

/** Whole CALENDAR days from today until `date` (>= 0); 0 for any time today.
 *  Compares midnight-aligned dates so a fixture later TODAY reads 0 ("Live"),
 *  not 1 ("Tomorrow") — the old fractional-ms ceil rounded any partial day up. */
export function daysUntil(date: Date): number {
  const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = startOfDay(date) - startOfDay(new Date());
  return Math.max(0, Math.round(diff / 86_400_000));
}

// ─── Type ──────────────────────────────────────────────────────────
export function Kicker({ children, green }: { children: ReactNode; green?: boolean }): JSX.Element {
  return <span className={green ? "tn-kicker tn-kicker--green" : "tn-kicker"}>{children}</span>;
}

export function StatusPill({ children, live }: { children: ReactNode; live?: boolean }): JSX.Element {
  return (
    <span className="tn-status">
      <span className={live ? "tn-dot tn-dot--live" : "tn-dot"} aria-hidden />
      <Kicker green={live}>{children}</Kicker>
    </span>
  );
}

// ─── Header ────────────────────────────────────────────────────────
export function TennisHeader({
  eyebrow,
  title,
  meta,
}: {
  eyebrow: string;
  title: string;
  meta?: ReactNode;
}): JSX.Element {
  return (
    <header className="tn-head">
      <div className="tn-head__left">
        <span className="tn-mark">
          <Icon name="tennis" size={20} />
        </span>
        <div className="tn-head__titles">
          <Kicker>{eyebrow}</Kicker>
          <h1 className="tn-title">{title}</h1>
        </div>
      </div>
      {meta ? <div className="tn-head__meta">{meta}</div> : null}
    </header>
  );
}

// ─── Layout helpers ────────────────────────────────────────────────
export function Stack({ children }: { children: ReactNode }): JSX.Element {
  return <div className="tn-stack">{children}</div>;
}
export function Grid({ children }: { children: ReactNode }): JSX.Element {
  return <div className="tn-grid">{children}</div>;
}

// ─── Pilot strip (factual tournament window) ───────────────────────
export function PilotStrip(): JSX.Element {
  const d = daysUntil(R1_DATE);
  const r1 = d === 0 ? "Live now" : d === 1 ? "Tomorrow" : `in ${d} days`;
  return (
    <div className="tn-strip">
      <span className="tn-strip__name">French Open 2026 · Roland Garros</span>
      <span className="tn-kv">
        <span className="tn-kv__k">Round 1</span>
        <span className="tn-kv__v tn-kv__v--green">{r1}</span>
      </span>
      <span className="tn-kv">
        <span className="tn-kv__k">Opens</span>
        <span className="tn-kv__v">Sun 24 May</span>
      </span>
      <span className="tn-kv">
        <span className="tn-kv__k">Final</span>
        <span className="tn-kv__v">Sun 7 Jun</span>
      </span>
      <span className="tn-kv">
        <span className="tn-kv__k">Surface</span>
        <span className="tn-kv__v tn-kv__v--green">Clay</span>
      </span>
    </div>
  );
}

// ─── Card ──────────────────────────────────────────────────────────
export function Card({
  eyebrow,
  title,
  lead,
  accent,
  children,
  footer,
}: {
  eyebrow?: string;
  title?: ReactNode;
  lead?: ReactNode;
  accent?: boolean;
  children?: ReactNode;
  footer?: ReactNode;
}): JSX.Element {
  return (
    <section className={accent ? "tn-card tn-card--accent" : "tn-card"}>
      {(eyebrow || title) && (
        <div className="tn-card__head">
          {eyebrow ? <Kicker>{eyebrow}</Kicker> : null}
          {title ? <div className="tn-card__title">{title}</div> : null}
        </div>
      )}
      <div className="tn-card__body">
        {lead ? <p className="tn-card__lead">{lead}</p> : null}
        {children}
      </div>
      {footer ? <div className="tn-card__foot">{footer}</div> : null}
    </section>
  );
}

// ─── Stat tile ─────────────────────────────────────────────────────
type Tone = "green" | "pos" | "muted" | "default";
export function StatTile({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
}): JSX.Element {
  const vClass = tone === "default" ? "tn-tile__v" : `tn-tile__v tn-tile__v--${tone}`;
  return (
    <div className="tn-tile">
      <Kicker>{label}</Kicker>
      <div className={vClass}>{value}</div>
      {sub ? <div className="tn-tile__sub">{sub}</div> : null}
    </div>
  );
}

// ─── Badge ─────────────────────────────────────────────────────────
export type BadgeTone = "pos" | "warn" | "neg" | "info" | "tennis" | "muted";
export function Badge({ tone = "muted", dot, children }: { tone?: BadgeTone; dot?: boolean; children: ReactNode }): JSX.Element {
  return (
    <span className={`tn-badge tn-badge--${tone}`}>
      {dot ? <span className="tn-badge__dot" aria-hidden /> : null}
      {children}
    </span>
  );
}

// ─── Legend ────────────────────────────────────────────────────────
export interface LegendItem {
  readonly badge: ReactNode;
  readonly title: string;
  readonly body: string;
}
export function Legend({ items }: { items: readonly LegendItem[] }): JSX.Element {
  return (
    <div className="tn-legend">
      {items.map((it, i) => (
        <div className="tn-legend__row" key={i}>
          <div className="tn-legend__label">
            <div>{it.badge}</div>
            <div className="tn-legend__title">{it.title}</div>
          </div>
          <div className="tn-legend__body">{it.body}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Round-ladder timeline ─────────────────────────────────────────
export interface TimelineItem {
  readonly round: string;
  readonly when: string;
  readonly key?: boolean;
}
export function Timeline({ items }: { items: readonly TimelineItem[] }): JSX.Element {
  return (
    <div className="tn-timeline">
      {items.map((it) => (
        <div className={it.key ? "tn-tl tn-tl--key" : "tn-tl"} key={it.round}>
          <span className="tn-tl__node" aria-hidden />
          <span className="tn-tl__round">{it.round}</span>
          <span className={it.key ? "tn-tl__when tn-tl__when--green" : "tn-tl__when"}>{it.when}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Awaiting / pre-open panel ─────────────────────────────────────
// Intentional pre-open state — optional countdown + ghost rows make the
// "not yet live" honest and designed, never fake signals.
export function AwaitingPanel({
  eyebrow = "Awaiting data",
  title,
  body,
  countdown,
  ghosts = 0,
  ghostTag = "Opens Round 1",
}: {
  eyebrow?: string;
  title: ReactNode;
  body: ReactNode;
  countdown?: { value: ReactNode; label: string };
  ghosts?: number;
  ghostTag?: string;
}): JSX.Element {
  return (
    <div className="tn-await">
      <div className="tn-await__head">
        <div>
          <StatusPill>{eyebrow}</StatusPill>
          <div className="tn-await__title">{title}</div>
          <p className="tn-await__body">{body}</p>
        </div>
        {countdown ? (
          <div className="tn-await__countdown">
            <div className="tn-await__count">{countdown.value}</div>
            <span className="tn-await__count-l">{countdown.label}</span>
          </div>
        ) : null}
      </div>
      {ghosts > 0 ? (
        <div className="tn-ghosts">
          {Array.from({ length: ghosts }).map((_, i) => (
            <div className="tn-ghost" key={i}>
              <div className="tn-ghost__bars">
                <span className="tn-ghost__bar tn-ghost__bar--w1" />
                <span className="tn-ghost__bar tn-ghost__bar--w2" />
              </div>
              <span className="tn-ghost__tag">{ghostTag}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─── Upsell note ───────────────────────────────────────────────────
export function UpsellNote({ children, cta = "See plans" }: { children: ReactNode; cta?: string }): JSX.Element {
  return (
    <div className="tn-upsell">
      <span className="tn-upsell__txt">
        <Icon name="lock" size={14} />
        {children}
      </span>
      <Link to="/pricing" className="tn-cta">
        {cta} →
      </Link>
    </div>
  );
}
